import { useCallback, useEffect, useState } from "react";

import {
  API_EMPLOYEE_TECH_STACK_LIST,
  API_EMPLOYEE_TECH_STACK_MASTER,
} from "../../../config/Endpoints.js";
import { getApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import type {
  EmployeeTechStackRow,
  TechStackCategory,
  TechStackFilters,
} from "./tech-stack-types.ts";

type PageResponse = {
  content?: EmployeeTechStackRow[];
  totalElements?: number;
  totalPages?: number;
};

export function useTechStackMaster() {
  const [categories, setCategories] = useState<TechStackCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getApi<unknown>(
      API_EMPLOYEE_TECH_STACK_MASTER,
      accessToken(),
      "json",
      controller.signal,
    )
      .then(({ data }) => {
        const payload =
          data && typeof data === "object" && "data" in data
            ? (data as { data?: unknown }).data
            : data;
        setCategories(Array.isArray(payload) ? (payload as TechStackCategory[]) : []);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError("Failed to load tech stack options.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  return { categories, loading, error };
}

export function useTechStackList(
  filters: TechStackFilters,
  page: number,
  pageSize: number,
) {
  const [rows, setRows] = useState<EmployeeTechStackRow[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  const refetch = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({
      page: String(Math.max(0, page - 1)),
      size: String(pageSize),
    });
    if (filters.employeeId) params.set("employeeId", filters.employeeId);
    if (filters.categoryId) params.set("categoryId", filters.categoryId);
    if (filters.techStackId) params.set("techStackId", filters.techStackId);

    if (filters.techStackAssigned === "true" || filters.techStackAssigned === "false") {
      params.set("techStackAssigned", filters.techStackAssigned);
    }
    if (filters.technicalStatus === "true" || filters.technicalStatus === "false") {
      params.set("technicalStatus", filters.technicalStatus);
    }
    if (filters.location) params.set("location", filters.location);

    setLoading(true);
    setError(null);
    getApi<PageResponse>(
      `${API_EMPLOYEE_TECH_STACK_LIST}?${params.toString()}`,
      accessToken(),
      "json",
      controller.signal,
    )
      .then(({ data }) => {
        const payload =
          data && typeof data === "object" && "data" in data
            ? ((data as PageResponse & { data?: PageResponse }).data ?? data)
            : data;
        setRows(Array.isArray(payload.content) ? payload.content : []);
        setTotalElements(Number(payload.totalElements) || 0);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setRows([]);
          setTotalElements(0);
          setError("Failed to load employee tech stacks.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [filters, page, pageSize, revision]);

  return { rows, totalElements, loading, error, refetch };
}
